import Offcanvas from 'react-bootstrap/Offcanvas';
import { useUiStore } from '../hooks/useUiStore';
import './offCanvas.scss';
import {
  fetchmppId,
  getColorClass,
  getOffcanvasTitle,
} from '../helpers/offCanvasHelper';
import DosageChangeSection from '../pages/PatientDetails/components/Timeline/DosageChangeSection';
import EPPOCDetailsContent from '../pages/PatientDetails/components/Timeline/contents/EPPOCDetailsContent';
import EReferralNotesContent from '../pages/PatientDetails/components/Timeline/contents/EReferralNotesContent';
import TimelinePopupHeader from '../pages/PatientDetails/components/Timeline/headers/TimelinePopupHeader';
import ProfileDateHeader from '../pages/PatientDetails/components/Timeline/headers/ProfileDateHeader';
import ConversationsContent from '../pages/PatientDetails/components/Timeline/contents/ConversationsContent';
import {
  CommonContent,
  DosageChangeContent,
  GenericDetailContent,
  MBSContent,
  MedicationContent,
  MedProfileContent,
  PathologyDiagnosticContent,
  PrescriptionContent,
  ProfileCheckContent,
  WellbeingContent,
} from '../pages/PatientDetails/components/Timeline/contents/ContentHelper';
import { useSelector } from 'react-redux';
import { TimelineTypes } from './Timeline/timeLineTypes';
import { Fragment } from 'react/jsx-runtime';
import { PathologyTimelineTable } from './PathologyTimelineTable';
import moment from 'moment';

export const TimelineOffCanvas = (props) => {
  const { isInsightDetailsOpen, insightDetailsContent } = useUiStore();
  const { insightSettings } = useSelector((state) => state.insightSettings);
  const { requestRefresh } = props;

  function closeInsightDetails() {
    props.closeInsightDetails();
  }

  function gotoMedSettings() {
    closeInsightDetails();
    if (typeof props.sendToParent === 'function') {
      props.sendToParent('6');
    }
  }

  const groupType = String(insightDetailsContent?.group_type);
  const itemGroupType = String(insightDetailsContent?.item_group_type);

  const isObservationView =
    groupType === TimelineTypes.OBSERVATIONS ||
    itemGroupType === TimelineTypes.OBSERVATIONS;

  const generateContent = (insightDetailsContent, showFullDay = false) => {
    const { group_type: groupType } = insightDetailsContent;
    if (insightDetailsContent?.pbs_item_id) {
      return (
        <MedicationContent insightDetailsContent={insightDetailsContent} />
      );
    } else if (insightDetailsContent?.settings?.DosageChangeSection) {
      return (
        <DosageChangeContent insightDetailsContent={insightDetailsContent} />
      );
    } else if (insightDetailsContent?.pd_prescription_record_link) {
      return (
        <PrescriptionContent insightDetailsContent={insightDetailsContent} />
      );
    } else if (
      insightDetailsContent?.event_category === 'Medicine Profile' ||
      (typeof insightDetailsContent?.id === 'string' &&
        insightDetailsContent?.id.startsWith('med-profile-'))
    ) {
      return (
        <MedProfileContent insightDetailsContent={insightDetailsContent} />
      );
    } else if (groupType === 'EPPOC MDS') {
      return (
        <EPPOCDetailsContent insightDetailsContent={insightDetailsContent} />
      );
    } else if (
      groupType === 'EReferral clerk note' ||
      insightDetailsContent?.event_name === 'eReferral clerk note'
    ) {
      return (
        <EReferralNotesContent insightDetailsContent={insightDetailsContent} />
      );
    } else if (insightDetailsContent?.detail) {
      return (
        <GenericDetailContent insightDetailsContent={insightDetailsContent} />
      );
    } else if (
      insightDetailsContent?.report_name &&
      insightDetailsContent?.document_id
    ) {
      return (
        <PathologyDiagnosticContent
          insightDetailsContent={insightDetailsContent}
        />
      );
    } else if (
      insightDetailsContent?.mbs_event ||
      insightDetailsContent?.mbs_item_id
    ) {
      return <MBSContent insightDetailsContent={insightDetailsContent} />;
    } else if (groupType?.startsWith('Profile')) {
      return (
        <ProfileCheckContent
          insightDetailsContent={insightDetailsContent}
          showFullDay={showFullDay}
        />
      );
    } else if (groupType?.startsWith('Wellbeing')) {
      return <WellbeingContent insightDetailsContent={insightDetailsContent} />;
    } else if (groupType === 'Conversations') {
      return (
        <ConversationsContent insightDetailsContent={insightDetailsContent} />
      );
    } else {
      return <CommonContent insightDetailsContent={insightDetailsContent} />;
    }
  };

  const generateListContent = (insightDetailsContent) => {
    const { children } = insightDetailsContent;

    // Group children by date (use event_date when available, fallback to start_time)
    const groups = children.reduce((acc, child) => {
      const dateStr = child.event_date
        ? moment(child.event_date).format('DD MMM YY')
        : moment(child.start_time).format('DD MMM YY');
      if (!acc[dateStr]) acc[dateStr] = [];
      acc[dateStr].push(child);
      return acc;
    }, {});

    // Sort group keys by chronological order based on earliest item's start_time
    const sortedDates = Object.keys(groups).sort((a, b) => {
      const aMin = Math.min(
        ...groups[a].map((c) =>
          c.start_time ? c.start_time : Date.parse(c.event_date),
        ),
      );
      const bMin = Math.min(
        ...groups[b].map((c) =>
          c.start_time ? c.start_time : Date.parse(c.event_date),
        ),
      );
      return aMin - bMin;
    });

    // Helper to extract a display name for alphabetical sorting (drug/event title)
    const getDrugName = (item) => {
      return (
        item.event_name ||
        '' ||
        item.medicinal_product_pack ||
        '' ||
        item.mpp_pt ||
        ''
      ).toString();
    };

    return (
      <div>
        {sortedDates.map((dateKey) => {
          const items = groups[dateKey].slice().sort((a, b) =>
            getDrugName(a).localeCompare(getDrugName(b), undefined, {
              sensitivity: 'base',
            }),
          );
          return (
            <div className="date-group" key={`date-group-${dateKey}`}>
              <div className="date date-group-header">{dateKey}</div>
              {items.map((child) => (
                <Fragment key={child.id}>
                  {generateContent(child, true)}
                </Fragment>
              ))}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <>
      <Offcanvas
        show={isInsightDetailsOpen}
        onHide={closeInsightDetails}
        placement="end"
        backdrop={false}
        scroll={true}
        className={`event-border ${getColorClass(groupType)} ${isObservationView ? 'offcanvas-half-screen' : ''}`}
      >
        <Offcanvas.Header>
          <TimelinePopupHeader
            gotoMedSettings={gotoMedSettings}
            closeInsightDetails={closeInsightDetails}
            insightDetailsContent={insightDetailsContent}
          />
        </Offcanvas.Header>

        <Offcanvas.Body>
          {getOffcanvasTitle(groupType, insightDetailsContent, itemGroupType)}
          {groupType.startsWith('Profile') && (
            <ProfileDateHeader
              date={insightDetailsContent.checks[0]?.interaction_date}
            />
          )}
          <div className="offcanvas-inner">
            <div className="group">
              {isObservationView ? (
                <PathologyTimelineTable requestRefresh={requestRefresh} />
              ) : groupType === TimelineTypes.MONTH_EVENTS ||
                groupType === TimelineTypes.WEEK_EVENTS ? (
                generateListContent(insightDetailsContent)
              ) : (
                generateContent(insightDetailsContent)
              )}
              {(insightDetailsContent.detail?.mpp_id ??
                insightDetailsContent.mpp_id ??
                fetchmppId(insightSettings, insightDetailsContent)) && (
                <DosageChangeSection
                  short={props.short}
                  closeInsightDetails={props.closeInsightDetails}
                />
              )}
            </div>
          </div>
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
};
